import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Avatar,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import axios from "axios";
import { hostname, parseError, toAvatarName, addImageDimensions } from "../utils";
import LoadingButton from "./LoadingButton";

interface UserImpersonationDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  onImpersonate: (customToken: string, superUserToken: string) => void;
}

interface SearchUser {
  id: string;
  email: string;
  displayName: string;
  photo?: string;
}

const UserImpersonationDialog: React.FC<UserImpersonationDialogProps> = ({
  open,
  onClose,
  getToken,
  onImpersonate,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<SearchUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [error, setError] = useState("");

  // Debounce search - only search after user stops typing for 500ms
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setUsers([]);
      return;
    }

    setLoading(true);
    const timeoutId = setTimeout(() => {
      searchUsers(searchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const searchUsers = async (query: string) => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/users/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { query },
      });

      setUsers(response.data.users || []);
      setLoading(false);
    } catch (err) {
      setError(parseError(err));
      setLoading(false);
    }
  };

  const handleImpersonate = async (uid: string) => {
    setImpersonating(true);
    setError("");

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/users/impersonate`,
        { uid },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Call the parent handler with both tokens
      onImpersonate(response.data.token, response.data.superUserToken);
      onClose();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setImpersonating(false);
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setUsers([]);
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Impersonate User</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Search by email or name"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Enter email or name to search..."
            helperText="Search for a user by their email address or display name"
          />
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        )}

        {!loading && users.length === 0 && searchQuery.length >= 2 && (
          <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
            No users found
          </Typography>
        )}

        {!loading && users.length > 0 && (
          <List sx={{ maxHeight: 400, overflow: "auto" }}>
            {users.map((user) => (
              <ListItem key={user.id} disablePadding>
                <ListItemButton
                  onClick={() => handleImpersonate(user.id)}
                  disabled={impersonating}
                >
                  <ListItemAvatar>
                    <Avatar src={addImageDimensions(user.photo, 80, 80)}>
                      {toAvatarName(user.displayName || user.email)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.displayName || "No name"}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          {user.email}
                        </Typography>
                        <br />
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          UID: {user.id}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {searchQuery.length < 2 && !loading && (
          <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
            Enter at least 2 characters to search
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={handleClose} variant="outlined">
          Cancel
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default UserImpersonationDialog;
